import jwtDecode from 'jwt-decode';
import React from 'react';

import authContext, { AuthContext } from './context';
import { Authenticator } from '../services/Authenticator';

/** Storage for additional login data */
const STORAGE_KEY = 'additional-login-data';

const getFromStorage = <T extends Record<string, unknown>>(): T => {
  const valueInStorage = window.sessionStorage.getItem(STORAGE_KEY);
  return valueInStorage ? JSON.parse(valueInStorage) : ({} as T);
};

const saveToStorage = (v: Record<string, unknown>) => {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(v));
};

export interface Props {
  children: React.ReactNode;
}

/**
 * <TokenAuthProvider> component
 */
const TokenAuthProvider: React.FunctionComponent<Props> = ({ children }) => {
  const [authenticating, setAuthenticating] = React.useState(false);
  const [authenticated, setAuthenticated] = React.useState(
    Authenticator.getSession().isValid(),
  );
  const [userData, setUserData] = React.useState(
    Authenticator.getSession().getUserData(),
  );
  const [additionalData, setAdditionalData] = React.useState<
    Partial<Record<string, unknown>>
  >(getFromStorage<Record<string, unknown>>());

  /**
   * Resets every piece of auth state, the additional data bag included.
   * However the session ends — explicit sign out or expiry — none of it may
   * survive into the next one: `updateAdditionalData` only ever merges, so a
   * key left behind here (a `roomId`, a `surveyId`) silently overrides the
   * next session's own value once it logs in.
   */
  const clearAuthState = React.useCallback(() => {
    setUserData(null);
    setAuthenticated(false);
    setAdditionalData({});
    window.sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  // Checks token validity (every 1s by default)
  React.useEffect(() => {
    if (!authenticated) {
      return () => null;
    }

    const id = window.setInterval(async () => {
      try {
        const session = await Authenticator.currentSession();
        if (!session.isValid()) {
          clearAuthState();
          await Authenticator.signOut();
          return;
        }

        // Update session data if it changed
        if (session.getUserData() !== userData) {
          setUserData(session.getUserData());
        }
      } catch (err) {
        // `currentSession` rejects on an expired or missing session, so this
        // is the branch an expiry actually lands in.
        clearAuthState();
      }
    }, 1000);

    return () => window.clearInterval(id);
  }, [authenticated, userData, clearAuthState]);

  /**
   * Updates the additional data state
   */
  const updateAdditionalData = React.useCallback<AuthContext['addData']>(
    (data) => {
      setAdditionalData((old) => {
        const value = { ...old, ...data };
        saveToStorage(value);
        return value;
      });
    },
    [],
  );

  /**
   * Sign in user, applying necessary state updates
   */
  const signIn = React.useCallback<AuthContext['signIn']>(
    async (token, options) => {
      try {
        setAuthenticating(true);
        const session = await Authenticator.signIn(token);

        // Decode original token for tracking
        const decoded = jwtDecode<{ tokenId: string }>(token);
        setUserData(session.userData);
        updateAdditionalData({ lastLoginToken: decoded.tokenId });
        setAuthenticated(true);

        if (options?.onCompleted) {
          options?.onCompleted(session);
        }
        return session;
      } catch (err) {
        if (options?.onError) {
          options?.onError(err as Error);
        }
        throw err;
      } finally {
        setAuthenticating(false);
      }
    },
    [updateAdditionalData],
  );

  /**
   * Sign out user, applying necessary state updates
   */
  const signOut = React.useCallback(async () => {
    try {
      setAuthenticating(true);
      await Authenticator.signOut();
      clearAuthState();
    } finally {
      setAuthenticating(false);
    }
  }, [clearAuthState]);

  /**
   * Prepare the auth value
   */
  const providerValue: AuthContext = React.useMemo(
    () => ({
      isAuthenticated: authenticated,
      loading: authenticating,
      userData: { ...(userData || {}), ...(additionalData || {}) },
      setAuthenticated,
      signIn,
      signOut,

      // Additional data handlers
      addData: updateAdditionalData,
      updateData: (k, v) => updateAdditionalData({ [k]: v }),
      removeData: (k) => updateAdditionalData({ [k]: undefined }),
    }),
    [
      authenticated,
      authenticating,
      userData,
      additionalData,
      signIn,
      signOut,
      updateAdditionalData,
    ],
  );

  return (
    <authContext.Provider value={providerValue}>
      {children}
    </authContext.Provider>
  );
};

export default TokenAuthProvider;
